import { useNavigate } from 'react-router-dom';
import { Button, Field } from '@plush-book/ui';
import { StepHeader } from '../components/StepHeader';
import { useGeneratorStore } from '../state/generatorStore';
import { findBlocklistMatches, PRICING } from '@plush-book/shared';

export function PlushUpload() {
  const nav = useNavigate();
  const plushies = useGeneratorStore((s) => s.plushies);
  const updatePlushie = useGeneratorStore((s) => s.updatePlushie);
  const addPlushie = useGeneratorStore((s) => s.addPlushie);
  const removePlushie = useGeneratorStore((s) => s.removePlushie);

  function next() {
    // simple text-blocklist gate
    const allText = plushies
      .map((p) => `${p.name} ${p.type} ${p.trait}`)
      .join(' ')
      .trim();
    const matches = findBlocklistMatches({ plushies: allText });
    if (matches.length > 0) {
      nav('/ip-error');
      return;
    }
    nav('/personal');
  }

  const allValid = plushies.every((p) => p.name.trim().length > 0 && p.ipChecked);

  return (
    <div className="shell">
      <StepHeader active={1} />
      <div className="scroll" style={{ maxWidth: 760, margin: '0 auto', width: '100%' }}>
        <h2
          style={{
            fontFamily: 'var(--serif)',
            fontSize: 28,
            fontWeight: 500,
            margin: 0,
            letterSpacing: -0.3,
          }}
        >
          Wer soll im Buch mitspielen?
        </h2>
        <p style={{ fontSize: 14, color: 'var(--ink-soft)', marginTop: 6, marginBottom: 22 }}>
          Lade Fotos hoch, damit daraus eine Buchfigur entstehen kann.
        </p>

        {plushies.map((p, i) => (
          <PlushieCard
            key={i}
            index={i}
            plush={p}
            canRemove={plushies.length > 1}
            onChange={(patch) => updatePlushie(i, patch)}
            onRemove={() => removePlushie(i)}
          />
        ))}

        <div
          style={{
            marginTop: 14,
            padding: 14,
            background: 'var(--sky)',
            borderRadius: 14,
            border: '1px solid var(--sky-deep)',
            fontSize: 12,
            color: 'var(--ink-soft)',
            lineHeight: 1.5,
          }}
        >
          <div style={{ fontWeight: 700, marginBottom: 4, color: 'var(--ink)' }}>
            Hinweis zu Markenfiguren
          </div>
          Bekannte Figuren wie Paw Patrol, Pokémon, Disney oder ähnliche Markenfiguren können wir
          leider nicht verwenden.
        </div>

        {plushies.length < PRICING.maxPlushiesPerBook && (
          <div
            style={{
              marginTop: 12,
              padding: 14,
              background: 'linear-gradient(135deg, var(--sand) 0%, var(--bg-card) 100%)',
              border: '1px solid var(--line)',
              borderRadius: 14,
            }}
          >
            <div style={{ fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 600 }}>
              Hat dein Kind noch ein zweites Lieblingskuscheltier?
            </div>
            <div style={{ fontSize: 12, color: 'var(--ink-soft)', marginTop: 4, lineHeight: 1.4 }}>
              Es darf in der Geschichte mitspielen.
            </div>
            <button
              onClick={() => addPlushie()}
              style={{
                width: '100%',
                marginTop: 10,
                padding: '12px',
                background: 'var(--bg-card)',
                border: '1.5px dashed var(--line)',
                borderRadius: 12,
                color: 'var(--ink-soft)',
                fontSize: 13,
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              + Weiteres Kuscheltier{' '}
              <span style={{ color: 'var(--terracotta-deep)' }}>+6,90 €</span>
            </button>
          </div>
        )}
        <div style={{ fontSize: 11, color: 'var(--ink-mute)', textAlign: 'center', marginTop: 8 }}>
          Maximal {PRICING.maxPlushiesPerBook} Kuscheltiere pro Buch
        </div>
      </div>
      <div className="sticky-cta" style={{ maxWidth: 760, margin: '0 auto', width: '100%' }}>
        <Button variant="dark" size="md" fullWidth disabled={!allValid} onClick={next}>
          Weiter →
        </Button>
      </div>
    </div>
  );
}

interface PlushieCardProps {
  index: number;
  plush: ReturnType<typeof useGeneratorStore.getState>['plushies'][number];
  canRemove: boolean;
  onChange: (patch: Partial<PlushieCardProps['plush']>) => void;
  onRemove: () => void;
}

function PlushieCard({ index, plush, canRemove, onChange, onRemove }: PlushieCardProps) {
  return (
    <div
      style={{
        background: 'var(--bg-card)',
        border: '1px solid var(--line)',
        borderRadius: 18,
        padding: 16,
        marginBottom: 12,
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 14,
        }}
      >
        <div style={{ fontFamily: 'var(--serif)', fontSize: 16, fontWeight: 600 }}>
          Kuscheltier {index + 1}
        </div>
        <span style={{ fontSize: 11, color: 'var(--ink-mute)', fontFamily: 'var(--mono)' }}>
          {index === 0 ? 'INKLUSIVE' : '+6,90 €'}
        </span>
      </div>

      {/* photo upload tiles (visual only — wire up file input next phase) */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 6,
          marginBottom: 8,
        }}
      >
        {[0, 1, 2].map((i) => (
          <label
            key={i}
            className="plush-placeholder"
            style={{
              aspectRatio: '1',
              borderRadius: 10,
              background: 'linear-gradient(135deg, #C8916A, #8E5B3E)',
              color: 'rgba(255,255,255,0.85)',
              position: 'relative',
              cursor: 'pointer',
            }}
          >
            <input
              type="file"
              accept="image/*"
              capture="environment"
              hidden
              onChange={() => onChange({ photoCount: Math.min(plush.photoCount + 1, 5) })}
            />
            <span style={{ fontSize: 9 }}>{['VORNE', 'SEITE', 'KOMPLETT'][i]}</span>
          </label>
        ))}
      </div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-mute)',
          lineHeight: 1.45,
          padding: '8px 10px',
          background: 'var(--sand)',
          borderRadius: 10,
          marginBottom: 14,
          marginTop: 8,
        }}
      >
        💡 Am besten von vorne, von der Seite und einmal komplett.
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <Field
          label="Name"
          value={plush.name}
          onChange={(e) => onChange({ name: e.target.value })}
        />
        <Field
          label="Art"
          value={plush.type}
          onChange={(e) => onChange({ type: e.target.value })}
          hint="Teddy, Hase, Dino, Hund, Katze, Fantasietier …"
        />
        <div>
          <label
            style={{
              fontSize: 12,
              color: 'var(--ink-soft)',
              fontWeight: 600,
              display: 'block',
              marginBottom: 6,
            }}
          >
            Anrede
          </label>
          <div style={{ display: 'flex', gap: 6 }}>
            {(['er', 'sie', 'es'] as const).map((p) => (
              <button
                key={p}
                onClick={() => onChange({ pronoun: p })}
                style={{
                  flex: 1,
                  padding: '10px',
                  textAlign: 'center',
                  borderRadius: 10,
                  border:
                    plush.pronoun === p
                      ? '1.5px solid var(--terracotta)'
                      : '1px solid var(--line)',
                  background: plush.pronoun === p ? 'var(--bg-card)' : 'transparent',
                  fontSize: 13,
                  fontWeight: 600,
                  color: plush.pronoun === p ? 'var(--terracotta-deep)' : 'var(--ink-soft)',
                  cursor: 'pointer',
                }}
              >
                {p}
              </button>
            ))}
          </div>
        </div>
        <Field
          label="Eigenschaft"
          value={plush.trait}
          onChange={(e) => onChange({ trait: e.target.value })}
          hint="optional · z.B. neugierig, kuschelig, tapfer"
        />
      </div>

      <label
        style={{
          marginTop: 12,
          padding: 14,
          background: 'var(--bg-card)',
          borderRadius: 14,
          border: '1px solid var(--line)',
          display: 'flex',
          gap: 10,
          cursor: 'pointer',
        }}
      >
        <input
          type="checkbox"
          checked={plush.ipChecked}
          onChange={(e) => onChange({ ipChecked: e.target.checked })}
          style={{ flexShrink: 0, marginTop: 2 }}
        />
        <span style={{ fontSize: 12, lineHeight: 1.5, color: 'var(--ink-soft)' }}>
          Ich bestätige, dass mein Kuscheltier keine bekannte Marken-, Film-, Serien-, Comic- oder
          Spielfigur ist.
        </span>
      </label>

      {canRemove && (
        <button
          onClick={onRemove}
          style={{
            marginTop: 10,
            background: 'transparent',
            border: 0,
            color: 'var(--ink-mute)',
            fontSize: 12,
            cursor: 'pointer',
          }}
        >
          Dieses Kuscheltier entfernen
        </button>
      )}
    </div>
  );
}
